import { useMemo } from "react";
import { LineChartConfig } from "@store/slices/line-chart-config-slice";
import { getLineColor, CHART_COLORS } from "@utils/chart-utils";

interface UseLineChartOptionsProps {
  config: LineChartConfig;
  isConfigPreview?: boolean;
  /** Viewer-selected rolling average window size override (0 = explicitly disabled). */
  viewerRollingAverage?: number | null;
  /** Viewer-selected time unit override. */
  viewerTimeUnit?: string | null;
}

export const useLineChartOptions = ({
  config,
  isConfigPreview = false,
  viewerRollingAverage = null,
  viewerTimeUnit = null,
}: UseLineChartOptionsProps) => {
  return useMemo(() => {
    // Color palette mapping - matches bar chart exactly
    const colorMap: Record<string, string> = {
      Orange: "#ffb09c",
      Green: "#57dfbe",
      Black: "#757679",
      Purple: "#a1a7df",
      Crimson: "#c36179",
      "Dark Green": "#57a68c",
      Blue: "#57bfe7",
      Yellow: "#ebdd7c",
    };

    // Resolve viewer overrides for rolling average and time unit.
    // viewerRollingAverage === 0 means "No Rolling Average" (explicitly disabled).
    const effectiveWindowSize =
      viewerRollingAverage !== null && viewerRollingAverage !== 0
        ? viewerRollingAverage
        : (config.rollingAverage?.windowSize ?? 0);
    const tooltipRAEnabled =
      viewerRollingAverage !== 0 &&
      (viewerRollingAverage !== null || (config.rollingAverage?.enabled && effectiveWindowSize > 0));
    const effectiveTimeUnit = viewerTimeUnit ?? config.timeUnit ?? "period";

    const isMulticolor = config.colorPalette === "Multicolor";
    const isCustomPalette = config.colorPalette === "Custom";

    // Check if statistical testing is enabled
    const statTestingEnabled = config.statisticalTesting?.enabled || false;
    const statMarkerStyle = config.statisticalTesting?.markerStyle ?? "arrows";

    // Get decimal places for rounding
    const decimals = config.percentageFormatting ?? 0;

    // Show % sign for Single/Multiple question types
    const showPercentSign =
      config.questionType?.toLowerCase() === "single" ||
      config.questionType?.toLowerCase() === "multi";

    // Order series based on lineOrder and sortingOption
    const orderedSeries = (() => {
      if (
        config.sortingOption === "manual" &&
        config.lineOrder &&
        config.lineOrder.length > 0
      ) {
        // Create a map for quick lookup
        const seriesMap = new Map(config.series.map((s) => [s.id, s]));
        // Order according to lineOrder
        return config.lineOrder
          .map((id) => seriesMap.get(id))
          .filter((s): s is (typeof config.series)[0] => s !== undefined);
      }
      // Use original order
      return config.series;
    })();

    // Filter visible series based on lineVisibility and apply custom labels
    const visibleSeries = orderedSeries
      .filter((series) => config.lineVisibility[series.id] !== false)
      .map((series, index) => {
        const paletteColors = isMulticolor
          ? [series.color || "#57bfe7"]
          : isCustomPalette
            ? CHART_COLORS
            : [colorMap[config.colorPalette] || "#57bfe7"];
        const seriesColor = getLineColor(
          index,
          series.id,
          isCustomPalette ? (config.customColors ?? {}) : null,
          paletteColors,
        );
        return {
          id: series.id,
          name:
            (config.customLineLabels && config.customLineLabels[series.id]) ||
            series.name,
          data:
            statTestingEnabled || config.rollingAverage?.enabled
              ? series.data.map((point, pointIndex) => ({
                  y:
                    point.y !== null ? Number(point.y.toFixed(decimals)) : null,
                  baseN: point.baseN,
                  missingRollingPeriodLabels: point.missingRollingPeriodLabels,
                  ...(statTestingEnabled && {
                    statSignificance: point.statSignificance,
                    periodLabel: config.categories[pointIndex],
                    priorYearLabel: point.priorYearLabel,
                    priorYearValue: point.priorYearValue,
                    priorYearInRange: point.priorYearInRange,
                    // Circles mode: only set per-point marker on significant points.
                    // Non-significant points MUST NOT have a marker property so they
                    // inherit the series-level marker.enabled (respecting showMarkers).
                    ...(statMarkerStyle === "circles" &&
                      point.statSignificance !== 0 &&
                      point.statSignificance !== undefined && {
                        marker:
                          point.statSignificance === -1
                            ? {
                                enabled: true,
                                radius: 4,
                                fillColor: "#ffffff",
                                lineWidth: 2,
                                lineColor: seriesColor,
                              }
                            : { enabled: true, radius: 4 },
                      }),
                    dataLabels: {
                      // Arrows mode: show ▲/▼; circles mode: no arrows at all
                      enabled:
                        statMarkerStyle === "arrows" &&
                        point.statSignificance !== undefined &&
                        point.statSignificance !== 0,
                      format:
                        point.statSignificance === 1
                          ? "▲"
                          : point.statSignificance === -1
                            ? "▼"
                            : "",
                      style: {
                        color:
                          point.statSignificance === 1
                            ? "#4caf50"
                            : point.statSignificance === -1
                              ? "#f44336"
                              : "#000",
                        fontSize: "14px",
                        fontWeight: "bold",
                        textOutline: "none",
                      },
                      y: -10,
                    },
                  }),
                }))
              : series.data.map((point) =>
                  point.y !== null ? Number(point.y.toFixed(decimals)) : null,
                ),
          color: seriesColor,
          marker: {
            enabled: config.showMarkers,
            radius: 4,
            symbol: "circle",
            ...(!config.showMarkers && {
              states: { hover: { enabled: false } },
            }),
          },
          lineWidth: 2,
        };
      });

    const options: Highcharts.Options = {
      chart: {
        type: "line",
        height: isConfigPreview ? 450 : undefined,
        backgroundColor: "transparent",
        style: {
          fontFamily: "Inter, sans-serif",
        },
        spacing: [10, 10, 10, 10],
      },
      title: {
        text: undefined, // Title is handled by widget header
      },
      subtitle: {
        text: undefined, // Subtitle is handled by widget header
      },
      xAxis: {
        categories: config.categories,
        title: {
          text: null,
        },
        labels: {
          style: {
            fontSize: "11px",
          },
          rotation: config.categories.length > 5 ? -65 : 0,
          align: "right",
        },
      },
      yAxis: {
        title: {
          text: null,
        },
        labels: {
          style: {
            fontSize: "12px",
          },
          formatter: function () {
            // Format Y-axis labels with the specified number of decimals
            const value =
              typeof this.value === "number"
                ? this.value
                : parseFloat(String(this.value));

            // When in custom range mode with float min/max values,
            // determine appropriate decimal places dynamically
            if (config.yAxisRangeMode === "custom") {
              // Check if min or max have decimal places
              const minHasDecimals =
                config.yAxisMin !== undefined &&
                !Number.isInteger(config.yAxisMin);
              const maxHasDecimals =
                config.yAxisMax !== undefined &&
                !Number.isInteger(config.yAxisMax);

              if (minHasDecimals || maxHasDecimals) {
                // Determine decimal places needed
                const minDecimals = minHasDecimals
                  ? (config.yAxisMin!.toString().split(".")[1] || "").length
                  : 0;
                const maxDecimals = maxHasDecimals
                  ? (config.yAxisMax!.toString().split(".")[1] || "").length
                  : 0;
                const neededDecimals = Math.max(
                  minDecimals,
                  maxDecimals,
                  decimals,
                );

                // Format with appropriate decimals, keeping trailing zeros
                return value.toFixed(neededDecimals);
              }
            }

            // Default: use the configured decimal places
            return value.toFixed(decimals);
          },
        },
        // Apply custom range in custom mode, or explicitly null to reset in auto mode
        min: config.yAxisRangeMode === "custom" ? config.yAxisMin : null,
        max: config.yAxisRangeMode === "custom" ? config.yAxisMax : null,
        // Force Highcharts to start exactly at min and end exactly at max
        startOnTick: config.yAxisRangeMode === "custom" ? false : true,
        endOnTick: config.yAxisRangeMode === "custom" ? false : true,
        // Ensure ticks align with the custom range
        tickAmount: config.yAxisRangeMode === "custom" ? undefined : undefined,
      },
      legend: {
        enabled: false, // Disable default legend, using custom sidebar instead
      },
      tooltip: {
        // Show tooltip for the specific point being hovered,
        // not all series for the given category/period
        shared: false,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderWidth: 1,
        borderColor: "#ccc",
        shadow: true,
        useHTML: true,
        formatter: function () {
          // "this" is a single point context when shared is false
          const point = this.point as any;
          const series = this.series as any;

          let tooltip = `<div style="padding: 8px;">`;

          const rawValue =
            this.y !== null && this.y !== undefined
              ? this.y
              : point && point.y !== undefined
                ? point.y
                : null;

          const value =
            rawValue !== null && rawValue !== undefined
              ? Number(rawValue).toFixed(decimals)
              : "N/A";

          const pointIndex = point.index;
          const currentPeriodLabel = this.x;
          const windowSize = effectiveWindowSize;
          const timeUnit = effectiveTimeUnit;

          // Helper function to get plural form
          const getPluralForm = (count: number, unit: string): string => {
            if (count === 1) return unit;
            if (unit === "month") return "months";
            return unit + "s";
          };

          // === BASELINE TOOLTIP (always shown for rolling average) ===
          if (tooltipRAEnabled && windowSize > 0) {
            // 1. Period label
            tooltip += `<strong>${currentPeriodLabel}</strong><br/>`;

            // 2. Rolling Average value
            tooltip += `<span style="color:${this.color}">●</span> ${series.name}: Rolling Average: <strong>${value}${showPercentSign ? "%" : ""}</strong><br/>`;

            // 3. Base (n) for rolling average
            if (point.options && typeof point.options === "object") {
              const pointData = point.options as any;
              if (pointData.baseN !== undefined && pointData.baseN !== null) {
                tooltip += `Base (n): ${pointData.baseN.toLocaleString()}<br/>`;
              }
            }

            // 4. Rolling window size and unit
            tooltip += `Rolling Average: ${windowSize} ${getPluralForm(windowSize, timeUnit)}<br/>`;

            // 5. Rolling window range for this point
            // Determine window range based on whether it's partial or full
            const isPartialWindow = pointIndex < windowSize - 1;

            if (isPartialWindow) {
              // Partial window: use available periods
              const periodLabels: string[] = [];
              for (let i = 0; i <= pointIndex; i++) {
                if (config.categories[i]) {
                  periodLabels.push(config.categories[i]);
                }
              }
              const startPeriod = periodLabels[0] || currentPeriodLabel;
              const endPeriod =
                periodLabels[periodLabels.length - 1] || currentPeriodLabel;
              tooltip += `Rolling Window: ${startPeriod} - ${endPeriod}<br/>`;
            } else {
              // Full window: go back (windowSize - 1) periods from current
              const startIndex = pointIndex - (windowSize - 1);
              const startPeriod =
                startIndex >= 0 && config.categories[startIndex]
                  ? config.categories[startIndex]
                  : currentPeriodLabel;
              tooltip += `Rolling Window: ${startPeriod} - ${currentPeriodLabel}<br/>`;
            }

            // === CONDITIONAL MESSAGES ===

            // 2.1 Partial rolling window coverage
            if (isPartialWindow) {
              const periodsUsed = pointIndex + 1;
              const periodLabels: string[] = [];
              for (let i = 0; i <= pointIndex; i++) {
                if (config.categories[i]) {
                  periodLabels.push(config.categories[i]);
                }
              }
              const periodRange =
                periodLabels.length > 0
                  ? `${periodLabels[0]} - ${periodLabels[periodLabels.length - 1]}`
                  : currentPeriodLabel;

              tooltip += `<span style="color:#666;">Partial Rolling Average: ${periodsUsed} of ${windowSize} ${getPluralForm(windowSize, timeUnit)}</span><br/>`;
              tooltip += `<span style="color:#666;">Using: ${periodRange}</span><br/>`;
            }

            // 2.2 Missing data within the window
            // Prefer per-point missingRollingPeriodLabels (passed via point.options in stat testing path),
            // fall back to global config for non-stat-testing path
            const perPointMissing =
              point.options && typeof point.options === "object"
                ? ((point.options as any).missingRollingPeriodLabels as
                    | string[]
                    | undefined)
                : undefined;
            const missingLabels =
              perPointMissing ?? config.missingRollingPeriodLabels;

            if (missingLabels && missingLabels.length > 0) {
              const periodsUsed = windowSize - missingLabels.length;

              // Map 0-based offsets to actual period labels:
              // offset 0 = current period, 1 = one period back, 2 = two periods back, etc.
              // Sort descending by offset so oldest period appears first (chronological order)
              const sortedMissingLabels = [...missingLabels].sort((a, b) => {
                const aNum = parseInt(a, 10);
                const bNum = parseInt(b, 10);
                if (!isNaN(aNum) && !isNaN(bNum)) return bNum - aNum;
                return 0;
              });
              const mappedMissingLabels = sortedMissingLabels.map(
                (offsetOrLabel) => {
                  const offset = parseInt(offsetOrLabel, 10);
                  if (!isNaN(offset)) {
                    const catIndex = pointIndex - offset;
                    if (catIndex >= 0 && config.categories[catIndex]) {
                      return config.categories[catIndex];
                    }
                    // Period is before the start of the categories array — calculate its label
                    const periodsBeforeStart = -catIndex;
                    if (config.startDate) {
                      const [yr, mo, dy] = config.startDate
                        .split("-")
                        .map(Number);
                      const d = new Date(yr, mo - 1, dy);
                      switch (timeUnit) {
                        case "month": {
                          d.setMonth(d.getMonth() - periodsBeforeStart);
                          return (
                            d.toLocaleString("default", { month: "short" }) +
                            " " +
                            d.getFullYear()
                          );
                        }
                        case "quarter": {
                          d.setMonth(d.getMonth() - periodsBeforeStart * 3);
                          const q = Math.floor(d.getMonth() / 3) + 1;
                          return `Q${q} ${d.getFullYear()}`;
                        }
                        case "semester": {
                          d.setMonth(d.getMonth() - periodsBeforeStart * 6);
                          const s = d.getMonth() < 6 ? 1 : 2;
                          return `Semester ${s} ${d.getFullYear()}`;
                        }
                        case "week": {
                          d.setDate(d.getDate() - periodsBeforeStart * 7);
                          const startOfYear = new Date(d.getFullYear(), 0, 1);
                          const weekNum = Math.ceil(
                            ((d.getTime() - startOfYear.getTime()) / 86400000 +
                              startOfYear.getDay() +
                              1) /
                              7,
                          );
                          return `Week ${weekNum} ${d.getFullYear()}`;
                        }
                      }
                    }
                    return `${offset} period(s) back`;
                  }
                  // Already a label string
                  return offsetOrLabel;
                },
              );

              tooltip += `<span style="color:#666;">Rolling average calculated using ${periodsUsed} of ${windowSize} ${getPluralForm(windowSize, timeUnit)} (missing: [${mappedMissingLabels.join(", ")}])</span><br/>`;
            }
          } else {
            // Non-rolling average: simple tooltip
            tooltip += `<strong>${currentPeriodLabel}</strong><br/>`;
            tooltip += `<span style="color:${this.color}">●</span> ${series.name}: <strong>${value}${showPercentSign ? "%" : ""}</strong><br/>`;
          }

          // === STATISTICAL TESTING MESSAGES ===
          if (
            statTestingEnabled &&
            point &&
            point.options &&
            typeof point.options === "object"
          ) {
            const pointData = point.options as any;
            const timeStrategy =
              config.statisticalTesting?.timeStrategy ?? "PREVIOUS_PERIOD";

            if (timeStrategy === "YEAR_OVER_YEAR") {
              // === YoY TOOLTIP ===
              const hasMarker =
                pointData.statSignificance !== undefined &&
                pointData.statSignificance !== 0;

              if (hasMarker) {
                const marker =
                  statMarkerStyle === "circles"
                    ? pointData.statSignificance === 1
                      ? "●"
                      : "○"
                    : pointData.statSignificance === 1
                      ? "▲"
                      : "▼";
                const color =
                  pointData.statSignificance === 1 ? "#4caf50" : "#f44336";
                tooltip += `<br/><span style="color:${color}; font-weight: bold;">${marker}</span>`;

                if (
                  pointData.baseN !== undefined &&
                  pointData.baseN !== null
                ) {
                  tooltip += `<br/>Base (current): ${pointData.baseN.toLocaleString()}`;
                }

                const significanceLevel =
                  config.statisticalTesting?.significanceLevel || "90%";
                const sigPct = significanceLevel.replace("%", "");
                const direction =
                  pointData.statSignificance === 1
                    ? "higher"
                    : "lower";
                const priorLabel = pointData.priorYearLabel ?? "prior year";
                const priorVal =
                  pointData.priorYearValue !== undefined &&
                  pointData.priorYearValue !== null
                    ? `(${Number(pointData.priorYearValue).toFixed(decimals)}${showPercentSign ? "%" : ""})`
                    : "";
                const notInView =
                  pointData.priorYearInRange === false
                    ? " (not in current view)"
                    : "";

                tooltip += `<br/><span style="color:${color};">${currentPeriodLabel} is statistically significantly ${direction} than ${priorLabel} ${priorVal}${notInView} at ${sigPct}% significance level.</span>`;
              } else {
                // No marker — show baseN only
                if (
                  pointData.baseN !== undefined &&
                  pointData.baseN !== null
                ) {
                  tooltip += `<br/>Base (current): ${pointData.baseN.toLocaleString()}`;
                }
              }
            } else {
              // === PREVIOUS_PERIOD TOOLTIP (existing logic) ===
              if (
                pointData.statSignificance !== undefined &&
                pointData.statSignificance !== 0
              ) {
                const marker =
                  statMarkerStyle === "circles"
                    ? pointData.statSignificance === 1
                      ? "●"
                      : "○"
                    : pointData.statSignificance === 1
                      ? "▲"
                      : "▼";
                const color =
                  pointData.statSignificance === 1 ? "#4caf50" : "#f44336";
                tooltip += `<span style="color:${color}; font-weight: bold;">${marker}</span> `;

                // Get previous period info
                const currentIndex = point.index;
                const previousIndex = currentIndex - 1;
                const hasPreviousPeriod = previousIndex >= 0;
                const previousPeriod = hasPreviousPeriod
                  ? config.categories[previousIndex]
                  : null;

                // 3.1 Previous rolling average value and window range
                if (tooltipRAEnabled && windowSize > 0) {
                  // Get previous point's value if available
                  const previousPoint = series.data[previousIndex];
                  const previousValue =
                    previousPoint?.y !== undefined &&
                    previousPoint?.y !== null
                      ? Number(previousPoint.y).toFixed(decimals)
                      : "N/A";

                tooltip += `<br/>Previous Rolling Average: <strong>${previousValue}${showPercentSign ? "%" : ""}</strong>`;

                  // Previous window range
                  const prevIsPartial = previousIndex < windowSize - 1;
                  if (prevIsPartial) {
                    const periodLabels: string[] = [];
                    for (let i = 0; i <= previousIndex; i++) {
                      if (config.categories[i]) {
                        periodLabels.push(config.categories[i]);
                      }
                    }
                    const startPeriod = periodLabels[0] || previousPeriod;
                    const endPeriod =
                      periodLabels[periodLabels.length - 1] ||
                      previousPeriod;
                    tooltip += `<br/>Previous Window: ${startPeriod} - ${endPeriod}`;
                  } else {
                    const prevStartIndex = previousIndex - (windowSize - 1);
                    const prevStartPeriod =
                      prevStartIndex >= 0 &&
                      config.categories[prevStartIndex]
                        ? config.categories[prevStartIndex]
                        : previousPeriod;
                    tooltip += `<br/>Previous Window: ${prevStartPeriod} - ${previousPeriod}`;
                  }
                }

                // 3.2 Base (n) for current and previous windows
                if (
                  pointData.baseN !== undefined &&
                  pointData.baseN !== null
                ) {
                  tooltip += `<br/>Base (current): ${pointData.baseN.toLocaleString()}`;
                }

                // Get previous point's baseN if available
                if (hasPreviousPeriod && series.data[previousIndex]) {
                  const prevPointData =
                    series.data[previousIndex].options ||
                    series.data[previousIndex];
                  if (
                    prevPointData &&
                    prevPointData.baseN !== undefined &&
                    prevPointData.baseN !== null
                  ) {
                    tooltip += `<br/>Base (previous): ${prevPointData.baseN.toLocaleString()}`;
                  }
                }

                // 3.3 Significance statement
                const significanceLevel =
                  config.statisticalTesting?.significanceLevel || "90%";
                const comparisonText =
                  pointData.statSignificance === 1
                    ? "Significantly higher than"
                    : "Significantly lower than";

                tooltip += `<br/><span style="color:${color}; font-weight: bold;">${comparisonText} previous rolling average at ${significanceLevel} level</span>`;

                // 3.4 Note if previous window is partial
                if (
                  tooltipRAEnabled &&
                  windowSize > 0 &&
                  previousIndex < windowSize - 1
                ) {
                  const prevPeriodsUsed = previousIndex + 1;
                  tooltip += `<br/><span style="color:#666;">Previous period: ${previousPeriod} (Partial: ${prevPeriodsUsed} of ${windowSize} ${getPluralForm(windowSize, timeUnit)})</span>`;
                  tooltip += `<br/><span style="color:#666;">Note: Comparison is against a partial rolling average.</span>`;
                }
              }
            }
          }

          tooltip += "</div>";
          return tooltip;
        },
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: statTestingEnabled, // Enable data labels when stat testing is on
          },
          enableMouseTracking: true,
        },
        series: {
          connectNulls: false, // Don't connect lines across null values
          states: {
            hover: {
              lineWidthPlus: 1,
            },
          },
        },
      },
      series: visibleSeries as any,
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false, // Disable hamburger menu
      },
    };

    return options;
  }, [config, isConfigPreview, viewerRollingAverage, viewerTimeUnit]);
};
